import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { LeafModal } from "../../../common/leaf/LeafModal/LeafModal";
import ServerSideExportMenu, {
  ExportButtonPayload,
  ExportDetailsPayload,
} from "../ServerSideExportCommon/ServerSideExportMenu/ServerSideExportMenu";
import ServerSideExportDownload from "../ServerSideExportCommon/ServerSideExportDownload/ServerSideExportDownload";
import { storedLocale } from "../../Authentication/util";
import { useAppSelector } from "../../../store";
import {
  CURRENT_VIEW,
  FULL_MAP,
  FULL_MAP_SELECTED_KPIS,
  FORMAT_XLSX,
  FORMAT_CSV,
  FORMAT_PDF,
  ExportFormat,
} from "../../../common/constants/export-constants";
import { getTranslatedFormats } from "../ServerSideExportCommon/ServerSideExportUtil/ServerSideExportFormatUtil";
import {
  useCurrentViewExportV2Mutation,
  useFullmapExportV2Mutation,
  useGetExportListV2Query,
} from "../../../services/ExportV2/exportV2API";
import {
  CurrentViewExportReqBody,
  ExportDetails,
  FullMapExportReqBody,
} from "../../../services/ExportV2/exportV2API.types";
import {
  dispatchNotifyInProgressEvent,
  dispatchOpenExportV2ModalEvent,
} from "../ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import "./LOSExportModal.scss";
import defaultContent from "../../../content/serverSideExport";
import {
  exportAnotherMenuTypeTrack,
  exportDeleteTypeTrack,
  exportDownloadTypeTrack,
  exportViewMenuTypeTrack,
} from "../../../common/tealium/Export/ServerSideExportTrack";
import { updateInProgressIndicator, isExportDisabledBySessionSsoOrigin } from "../../../common/util/exportUtil";
import { store } from "../../Lit/acc-boot/acc-boot.js";
import { getSavedKpiPreferencesForView } from "../../../common/util/savedKpiPrefrencesUtil";
import { filterKpisByConfiguration } from "../../Lit/acc-wc-views/src/classes/support/getExportFields.js";
import { useGetSavedKpiPreferenceQuery } from "../../../services/KPIPreferences/savedKpiPreferencesAPI";

type Props = {
  open: boolean;
  onCloseCallbackFn: Function;
  view: "rwlos" | "edLos";
};

export const LOSExportModal = ({ open, onCloseCallbackFn, view }: Props) => {
  const { t } = useTranslation(["serverSideExport"]);

  const moduleId = "los";
  const locale = storedLocale();

  const boot = useAppSelector(({ boot }) => boot);
  const { user } = boot;
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { profile } = useAppSelector((store) => store?.boot?.configuration);
  const { savedKpiPreferencesData } = useAppSelector((store) => store?.savedKpiPreferencesData);
  const savedKpiViewName = "accWcLosCustomizedFields";
  useGetSavedKpiPreferenceQuery(savedKpiViewName, { skip: !open, refetchOnMountOrArgChange: true });

  // Replace the acc-wc-los-header's role: build export details from React state
  // (previously supplied by the Lit acc-wc-los-header, which is no longer rendered for LOS)
  const getExportDetails = (): ExportDetailsPayload => {
    // Build fixed export fields from the active LOS view configuration.
    const viewConfiguration = boot.configuration?.[view];
    const configuredDates =
      viewConfiguration && "uiProfileDates" in viewConfiguration ? (viewConfiguration.uiProfileDates ?? "") : "";
    const LOS_DEFAULT_FIELDS = [
      "level",
      "sponsorABONo",
      "aboNo",
      "countryCode",
      "name",
      "entryDate",
      "phone",
      "email",
      "address",
      ...(configuredDates.split(",").includes("renewal_date") ? ["renewalDate"] : []),
    ];
    const storeState = (store as any).getState?.() ?? {};
    const { hideVolume = false, availableKpis: storeAvailableKpis = [] } = storeState;

    const getFields = (isFullMap: boolean, exportSelectedKpis: boolean): string[] => {
      const savedKpiSelection = filterKpisByConfiguration(
        savedKpiPreferencesData ? getSavedKpiPreferencesForView(savedKpiViewName, savedKpiPreferencesData) : [],
        storeAvailableKpis,
      );

      if (!isFullMap && hideVolume === false && savedKpiSelection.length > 0) {
        return [...LOS_DEFAULT_FIELDS, ...savedKpiSelection];
      }
      if (isFullMap && hideVolume === false) {
        if (exportSelectedKpis) {
          return [...LOS_DEFAULT_FIELDS, ...savedKpiSelection];
        }
        return [...LOS_DEFAULT_FIELDS, ...storeAvailableKpis];
      }
      return LOS_DEFAULT_FIELDS;
    };

    // marketType must reflect the live filter state (inInternational from acc-boot store),
    // NOT the static uiGrid config — the user may have toggled the international filter.
    const inInternational = storeState.inInternational ?? false;
    const marketType = inInternational ? "international" : "in-market";

    // Build currentViewList by replicating AccWcLosHeader.__updateExportState:
    // reach into acc-wc-los-ar → #component → firstElementChild to get the grid's live __data.
    // This is the same technique the old acc-wc-los-header used.
    let currentViewList: Array<{ affAbo: string; level: number; sponsorAbo: string }> = [];
    try {
      const losArEl = document.getElementById("acc_wc_los_ar_component");
      const targetView = losArEl?.shadowRoot?.querySelector("#component")?.firstElementChild as any;
      if (targetView?.__data?.length > 0) {
        currentViewList = targetView.__data.map((u: any) => ({
          affAbo: u.affAbo,
          level: u.inode?.level ?? 1,
          sponsorAbo: `${u.sponsoring?.abo ?? 0}`,
        }));
      }
    } catch (_) {
      // DOM not ready or structure changed — fall through to default
    }

    if (currentViewList.length === 0) {
      // Also try acc-boot store as secondary fallback (set by grid in some configurations)
      const exportAffAbos: Array<{ affAbo: string; level?: number; sponsorAbo?: string }> =
        storeState.exportAffAbos ?? [];
      if (exportAffAbos.length > 0) {
        currentViewList = exportAffAbos.map((a) => ({
          affAbo: a.affAbo,
          level: a.level ?? 1,
          sponsorAbo: `${a.sponsorAbo ?? "0"}`,
        }));
      } else {
        currentViewList = [{ affAbo: `${user.aff}-${user.abo}`, level: 1, sponsorAbo: "0" }];
      }
    }

    const businessInView = storeState.businessInView ?? {};
    const reportAffAbo =
      businessInView.aff && businessInView.abo
        ? `${businessInView.aff}-${businessInView.abo}`
        : `${user.aff}-${user.abo}`;

    return {
      fullmap: {
        avaliableKPIs: getFields(true, false),
        selectedKPIs: getFields(true, true),
        marketType,
      },
      currentView: {
        currentViewList,
        avaliableKPIs: getFields(false, false),
        selectedKPIs: getFields(false, true),
      },
      reportAffAbo,
      moduleId,
    };
  };

  const [fullmapExport] = useFullmapExportV2Mutation();
  const [currentViewExport] = useCurrentViewExportV2Mutation();
  const { los: isExportDisabled } = isExportDisabledBySessionSsoOrigin();

  const {
    isFetching,
    data: exportListData,
    refetch,
  } = useGetExportListV2Query({ moduleId }, { skip: isExportDisabled });

  const [showServerSideExportMenu, setShowServerSideExportMenu] = useState(true);

  updateInProgressIndicator(exportListData?.Exports);

  // when open modal, decide which screen to display
  useEffect(() => {
    if (open) {
      const showExportMenu = exportListData?.Exports?.length === 0;
      setShowServerSideExportMenu(showExportMenu);
    }
  }, [open, exportListData]);

  // refetch the export list when change business country
  useEffect(() => {
    if (!isExportDisabled) {
      refetch();
    }
  }, [user, refetch, isExportDisabled]);

  const options = [
    {
      id: FULL_MAP,
      text: `${t("exportTypeModal.menu.los.all", defaultContent.exportTypeModal.menu.los.all)}`,
    },
    {
      id: FULL_MAP_SELECTED_KPIS,
      text: `${t("exportTypeModal.menu.los.selectedKPIs", defaultContent.exportTypeModal.menu.los.selectedKPIs)}`,
    },
    {
      id: CURRENT_VIEW,
      text: `${t("exportTypeModal.menu.los.currentView", defaultContent.exportTypeModal.menu.los.currentView)}`,
    },
  ];

  const showViewExportsButton = (exportListData?.Exports ?? []).length > 0;

  const onExportAnotherClick = () => {
    exportAnotherMenuTypeTrack(moduleId);
    setShowServerSideExportMenu(true);
  };

  const formatExportFieldID = (field: string) => {
    switch (field) {
      case "veVolumePV":
        return "volumeEquivalencyPV";
      default:
        return field;
    }
  };

  const getKPIsContent = (kpis: string[]) => {
    const translatedFields = t("exportFields", {
      returnObjects: true,
      defaultValue: {},
    }) as Record<string, string>;
    const exportFields = { ...defaultContent.exportFields, ...translatedFields };

    return kpis.map((id) => ({
      id: formatExportFieldID(id),
      title: exportFields[id] || "not available",
    }));
  };

  const getKPIs = (payload: ExportButtonPayload) => {
    const { selectedId, fullmap, currentView } = payload;

    switch (selectedId) {
      case FULL_MAP:
        return getKPIsContent(fullmap.avaliableKPIs);
      case FULL_MAP_SELECTED_KPIS:
        return getKPIsContent(fullmap.selectedKPIs);
      default:
        return getKPIsContent(currentView.selectedKPIs);
    }
  };

  const getExportPayload = (payload: ExportButtonPayload) => {
    const { selectedId, fullmap, currentView, reportAffAbo } = payload;

    const { address, addressLinesDivider } = profile?.format || {};

    // strictInMarket must reflect the live filter state, not the static uiGrid config.
    // inInternational=true means "Show International Frontlines" is ON → NOT strict in-market.
    const liveStoreState = (store as any).getState?.() ?? {};
    const inInternational = liveStoreState.inInternational ?? false;
    const strictInMarket = !inInternational;
    const isoCountryCode = boot?.user?.isoCountryCode || "";

    const textOverride = t("textOverride", { returnObjects: true, defaultValue: defaultContent.textOverride });
    const fields = getKPIs(payload);

    const exportBodyBased = {
      moduleId,
      reportAffAbo,
      period: selectedPeriod,
      locale,
      textOverride,
      fields,
      strictInMarket,
      countryCode: isoCountryCode,
      format: {
        address: {
          pattern: address,
          addressLineDivider: addressLinesDivider,
        },
      },
    };

    switch (selectedId) {
      case FULL_MAP:
        return {
          ...exportBodyBased,
          marketType: fullmap.marketType,
          filteredByKPIS: false,
        };
      case FULL_MAP_SELECTED_KPIS:
        return {
          ...exportBodyBased,
          marketType: fullmap.marketType,
          filteredByKPIS: true,
        };
      default:
        return {
          ...exportBodyBased,
          affAbos: currentView.currentViewList,
          filteredByKPIS: false,
        };
    }
  };

  const triggerExportRequest = async (payload: ExportButtonPayload) => {
    if (isExportDisabled) {
      return;
    }

    const { selectedId } = payload;
    const exportPayload = getExportPayload(payload);

    dispatchOpenExportV2ModalEvent(moduleId, false);
    setShowServerSideExportMenu(false);

    try {
      if (selectedId === CURRENT_VIEW) {
        await currentViewExport(exportPayload as CurrentViewExportReqBody);
      } else {
        await fullmapExport(exportPayload as FullMapExportReqBody);
      }
      dispatchNotifyInProgressEvent();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to trigger export for module "${moduleId}" (type: "${selectedId}"):`, error);
    }
  };

  const viewAllExportedDataButtonOnClick = () => {
    exportViewMenuTypeTrack(moduleId);
    setShowServerSideExportMenu(false);
  };

  const downloadButtonOnClick = (exportDetails: ExportDetails, fileFormat: string, moduleId: string) => {
    const { ExportType = "", FilteredByKPIS = false } = exportDetails;
    exportDownloadTypeTrack(ExportType, fileFormat, FilteredByKPIS, moduleId);
  };
  const supportedFormats: ExportFormat[] = [FORMAT_XLSX, FORMAT_CSV, FORMAT_PDF];

  const deleteButtonOnClick = (exportDetails: ExportDetails, moduleId: string) => {
    const { ExportType = "", ExportStatus, FilteredByKPIS = false } = exportDetails;
    exportDeleteTypeTrack(ExportType, ExportStatus, FilteredByKPIS, moduleId);
  };

  return (
    <div className="los-export-modal">
      <LeafModal open={open} onCloseCallbackFn={onCloseCallbackFn} id={"los-export-modal"}>
        <ServerSideExportMenu
          title={`${t("exportTypeModal.header", defaultContent.exportTypeModal.header)}`}
          subTitle={t("exportTypeModal.subheader", {
            formats: getTranslatedFormats(t, supportedFormats, defaultContent),
            defaultValue: defaultContent.exportTypeModal.subheader,
          })}
          options={options}
          getExportDetails={getExportDetails}
          exportButton={{
            text: `${t("exportTypeModal.exportButton", defaultContent.exportTypeModal.exportButton)}`,
            onClick: triggerExportRequest,
          }}
          show={showServerSideExportMenu}
          showExportButton={true}
          showViewExportsButton={showViewExportsButton}
          viewExportsButton={{
            text: `${t("exportTypeModal.viewExportButton", defaultContent.exportTypeModal.viewExportButton)}`,
            onClick: viewAllExportedDataButtonOnClick,
          }}
          isFetching={isFetching}
        />
        <ServerSideExportDownload
          title={`${t("availableExportModal.header", defaultContent.availableExportModal.header)}`}
          subTitle={t("availableExportModal.subheader", {
            formats: getTranslatedFormats(t, supportedFormats, defaultContent),
            defaultValue: defaultContent.availableExportModal.subheader,
          })}
          exportTypeContent={{
            fullMap: `${t(
              "exportTypeModal.menu.los.selectedKPIs",
              defaultContent.exportTypeModal.menu.los.selectedKPIs,
            )}`,
            fullMapAllKPIs: `${t("exportTypeModal.menu.los.all", defaultContent.exportTypeModal.menu.los.all)}`,
            currentView: `${t(
              "exportTypeModal.menu.los.currentView",
              defaultContent.exportTypeModal.menu.los.currentView,
            )}`,
          }}
          exports={exportListData?.Exports}
          downloadButtonOnClick={downloadButtonOnClick}
          deleteButtonOnClick={deleteButtonOnClick}
          show={showServerSideExportMenu === false}
          showExportAnotherButton={true}
          exportAnotherButton={{
            exportText: `${t("exportTypeModal.exportButton", defaultContent.exportTypeModal.exportButton)}`,
            exportAnotherText: `${t(
              "availableExportModal.exportAnotherButton",
              defaultContent.availableExportModal.exportAnotherButton,
            )}`,
            onClick: onExportAnotherClick,
          }}
          moduleId={moduleId}
          isFetching={isFetching}
        />
      </LeafModal>
    </div>
  );
};
